<template>
  <div class="unknown-issue-management">
    <ds-header title="未知问题管理"></ds-header>

    <!-- 统计卡片 -->
    <a-row v-if="statisticsList.length" :gutter="16" class="stat-cards">
      <a-col v-for="(item, index) in statisticsList" :key="index" :span="statColSpan">
        <div class="stat-card" :class="`stat-card--${getStatCardStyle(item.type).theme}`">
          <div class="stat-card__content">
            <div class="stat-card__title">{{ item.type }}</div>
            <div class="stat-card__value">{{ item.value }}</div>
            <div class="stat-card__desc">{{ item.desc }}</div>
          </div>
          <a-icon :type="getStatCardStyle(item.type).icon" class="stat-card__icon" />
        </div>
      </a-col>
    </a-row>

    <a-form layout="inline" class="search-form">
      <a-form-item label="问题内容：">
        <a-input
          v-model.trim="searchForm.question"
          placeholder="搜索问题内容..."
          allow-clear
          class="inputClass"
          @pressEnter="search"
        />
      </a-form-item>
      <a-form-item label="处理状态：">
        <a-select
          v-model="searchForm.status"
          placeholder="全部状态"
          style="width: 200px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" @click="search" style="margin-top: 3px">
          <a-icon type="search" />查询
        </a-button>
        <a-button style="margin-left: 15px" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>

    <a-table
      :rowKey="(record, index) => record.id || index"
      :columns="table.columns"
      :data-source="table.dataInfo"
      :pagination="table.pagination.total ? table.pagination : false"
      :loading="table.loading"
      :scroll="{ x: 1250 }"
      @change="pageChange"
      bordered
      style="margin-top: 15px"
    >
      <span slot="question" slot-scope="text">
        <span class="ellipsis-text" :title="text">{{ text || "-" }}</span>
      </span>
      <span slot="reasonType" slot-scope="text">
        <span class="ellipsis-text" :title="text">{{ text || "-" }}</span>
      </span>
      <span slot="rewrittenQuestion" slot-scope="text">
        <span class="ellipsis-text" :title="text">{{ text || "-" }}</span>
      </span>
      <span slot="status" slot-scope="text">
        <a-tag :color="getStatusConfig(text).color">
          {{ getStatusConfig(text).label }}
        </a-tag>
      </span>
      <span slot="createTime" slot-scope="text">
        <div class="time-cell">{{ formatDate(text) }}</div>
        <div class="time-cell time-sub">{{ formatTime(text) }}</div>
      </span>
    </a-table>
  </div>
</template>

<script>
import {
  getUnknownIssueStatistics,
  getUnknownIssueList,
} from "@/api/intelligentCustomerService";
import { UNKNOWN_ISSUE_STATUS, UNKNOWN_ISSUE_STATUS_MAP } from "../help";

const STAT_CARD_STYLE_MAP = {
  全部未知问题: { theme: "total", icon: "bar-chart" },
  待处理: { theme: "pending", icon: "hourglass" },
  已处理: { theme: "processed", icon: "check-square" },
  已忽略: { theme: "ignored", icon: "stop" },
  处理完成率: { theme: "rate", icon: "pie-chart" },
};

const DEFAULT_STAT_CARD_STYLE = { theme: "default", icon: "info-circle" };

export default {
  name: "unknownIssueManagement",
  data() {
    return {
      statusOptions: UNKNOWN_ISSUE_STATUS,
      searchForm: {
        question: "",
        status: undefined,
      },
      statisticsList: [],
      table: {
        loading: false,
        columns: [
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 110,
          },
          {
            title: "问题内容",
            dataIndex: "question",
            key: "question",
            ellipsis: true,
            width: 320,
            scopedSlots: { customRender: "question" },
          },
          {
            title: "未知原因描述",
            dataIndex: "reasonType",
            key: "reasonType",
            width: 160,
            scopedSlots: { customRender: "reasonType" },
          },
          {
            title: "改写问题",
            dataIndex: "rewrittenQuestion",
            key: "rewrittenQuestion",
            width: 300,
            scopedSlots: { customRender: "rewrittenQuestion" },
          },
          {
            title: "创建时间",
            dataIndex: "createTime",
            key: "createTime",
            width: 180,
            scopedSlots: { customRender: "createTime" },
          },
          {
            title: "处理状态",
            dataIndex: "status",
            key: "status",
            width: 140,
            scopedSlots: { customRender: "status" },
          },
        ],
        dataInfo: [],
        pagination: {
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
    };
  },
  computed: {
    statColSpan() {
      const len = this.statisticsList.length;
      if (!len) return 24;
      return Math.floor(24 / len);
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    getStatCardStyle(type) {
      return STAT_CARD_STYLE_MAP[type] || DEFAULT_STAT_CARD_STYLE;
    },
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    getStatusConfig(status) {
      return UNKNOWN_ISSUE_STATUS_MAP[status] || { label: "-", color: "default" };
    },
    formatDate(time) {
      if (!time) return "-";
      return String(time).split(" ")[0] || time;
    },
    formatTime(time) {
      if (!time) return "";
      const parts = String(time).split(" ");
      return parts[1] || "";
    },
    async initPage() {
      await this.fetchStatistics();
      await this.fetchList();
    },
    async fetchStatistics() {
      try {
        const res = await getUnknownIssueStatistics();
        if (this.isSuccess(res)) {
          this.statisticsList = Array.isArray(res.data) ? res.data : [];
        }
      } catch {
        this.statisticsList = [];
      }
    },
    search() {
      this.table.pagination.current = 1;
      this.fetchStatistics();
      this.fetchList();
    },
    resetSearchForm() {
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.searchForm = this.$options.data.call(this).searchForm;
      this.fetchStatistics();
      this.fetchList();
    },
    pageChange(option) {
      this.table.pagination.current = option.current;
      this.table.pagination.pageSize = option.pageSize;
      this.fetchList();
    },
    async fetchList() {
      this.table.loading = true;
      try {
        const params = {
          question: this.searchForm.question,
          status: this.searchForm.status,
          pageNo: this.table.pagination.current,
          pageSize: this.table.pagination.pageSize,
        };
        const res = await getUnknownIssueList(params);
        if (this.isSuccess(res)) {
          if (Array.isArray(res.data)) {
            this.table.dataInfo = res.data;
            this.table.pagination.total = res.data.length;
          } else {
            this.table.dataInfo = res.data?.list || res.data?.records || [];
            this.table.pagination.total = res.data?.total || 0;
          }
        }
      } finally {
        this.table.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.unknown-issue-management {
  .stat-cards {
    margin-bottom: 20px;
  }

  .stat-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    background: #fff;
    border-radius: 4px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    border-left-width: 4px;
    border-left-style: solid;

    &--total {
      border-left-color: #1890ff;
      .stat-card__icon {
        color: #1890ff;
      }
    }

    &--pending {
      border-left-color: #faad14;
      .stat-card__icon {
        color: #faad14;
      }
    }

    &--processed {
      border-left-color: #52c41a;
      .stat-card__icon {
        color: #52c41a;
      }
    }

    &--ignored {
      border-left-color: #d9d9d9;
      .stat-card__icon {
        color: #8c8c8c;
      }
    }

    &--rate {
      border-left-color: #722ed1;
      .stat-card__icon {
        color: #722ed1;
      }
    }

    &--default {
      border-left-color: #1890ff;
      .stat-card__icon {
        color: #1890ff;
      }
    }

    &__title {
      color: rgba(0, 0, 0, 0.45);
      font-size: 14px;
    }

    &__value {
      font-size: 28px;
      font-weight: 600;
      line-height: 1.2;
      margin: 8px 0 4px;
      color: rgba(0, 0, 0, 0.85);
    }

    &__desc {
      color: rgba(0, 0, 0, 0.45);
      font-size: 12px;
    }

    &__icon {
      font-size: 32px;
      opacity: 0.85;
    }
  }

  .search-form {
    margin-top: 4px;
  }

  .time-cell {
    line-height: 1.4;
  }

  .ellipsis-text {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  .time-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
}
</style>
